"use client";

import { useState } from "react";
import type { Locale } from "@/lib/i18n";
import { CONVERSATION_MAX_LENGTH, CONVERSATION_MIN_LENGTH, type AiConversationChemistry } from "@/lib/ai-conversation-chemistry";
import Icon from "./Icon";
type Props = {
  locale: Locale;
};
type ApiResponse = {
  result?: AiConversationChemistry;
  error?: string;
};
export default function AiConversationChemistryForm({
  locale
}: Props) {
  const [conversation, setConversation] = useState("");
  const [result, setResult] = useState<AiConversationChemistry | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = locale === "ko" ? {
    label: "대화 내용",
    placeholder: "예: 나: 이번 주말에 시간 돼?\\n상대: 음 아직 모르겠어 ㅎㅎ\\n나: 그럼 편할 때 알려줘!",
    button: "케미 분석하기",
    loading: "분석 중...",
    length: `${CONVERSATION_MIN_LENGTH}-${CONVERSATION_MAX_LENGTH}자`,
    error: "대화 케미를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.",
    invalid: "대화 내용을 20자 이상 입력해 주세요.",
    tempo: "대화 템포",
    signals: "보이는 신호",
    replies: "다음 답장 아이디어",
    privacy: "이 분석은 붙여 넣은 텍스트를 저장하지 않고 요청 처리에만 사용합니다."
  } : {
    label: "Conversation",
    placeholder: "Example:\\nMe: Are you free this weekend?\\nThem: Hmm not sure yet haha\\nMe: No pressure, let me know when it feels easy.",
    button: "Analyze chemistry",
    loading: "Analyzing...",
    length: `${CONVERSATION_MIN_LENGTH}-${CONVERSATION_MAX_LENGTH} chars`,
    error: "We could not load the conversation read. Please try again shortly.",
    invalid: "Please enter at least 20 characters.",
    tempo: "Conversation tempo",
    signals: "Observable signals",
    replies: "Reply ideas",
    privacy: "The pasted text is not stored and is only used to process this request."
  };
  async function submit() {
    const trimmed = conversation.trim();
    if (trimmed.length < CONVERSATION_MIN_LENGTH) {
      setError(copy.invalid);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const response = await fetch("/api/ai/conversation-chemistry", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          conversation: trimmed,
          locale
        })
      });
      const data = (await response.json()) as ApiResponse;
      if (!response.ok || !data.result) {
        throw new Error(data.error ?? "request failed");
      }
      setResult(data.result);
    } catch {
      setError(copy.error);
    } finally {
      setPending(false);
    }
  }
  return <section className="card flex flex-col gap-5">
      <label className="flex flex-col gap-2">
        <span className="font-display text-sm text-slate-800">
          {copy.label}
        </span>
        <textarea value={conversation} onChange={event => setConversation(event.target.value)} placeholder={copy.placeholder} maxLength={CONVERSATION_MAX_LENGTH} rows={8} className="min-h-52 resize-y rounded-2xl border border-brand-100 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
      </label>
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>{copy.privacy}</span>
        <span>{conversation.trim().length.toLocaleString()}</span>
      </div>
      <p className="text-xs text-slate-400">{copy.length}</p>
      <button type="button" onClick={submit} disabled={pending} className="btn-primary inline-flex min-h-11 items-center justify-center gap-2 disabled:opacity-60">
        <Icon name="message-circle-heart" size={16} />
        <span>{pending ? copy.loading : copy.button}</span>
      </button>

      {error ? <p role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </p> : null}

      {result ? <div className="flex flex-col gap-4 border-t border-brand-100 pt-5">
          <div>
            <h2 className="font-display text-lg text-slate-900">
              {result.title}
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              {result.summary}
            </p>
          </div>

          <div className="rounded-2xl bg-white/70 p-4 ring-1 ring-brand-100">
            <h3 className="font-display text-sm text-slate-800">
              {copy.tempo}
            </h3>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              {result.tempo}
            </p>
          </div>

          <div>
            <h3 className="font-display text-sm text-slate-800">
              {copy.signals}
            </h3>
            <ul className="mt-3 flex flex-col gap-2 text-sm leading-6 text-slate-600">
              {result.signals.map(signal => <li key={signal}>{signal}</li>)}
            </ul>
          </div>

          <div>
            <h3 className="font-display text-sm text-slate-800">
              {copy.replies}
            </h3>
            <div className="mt-3 grid gap-2">
              {result.replyIdeas.map(idea => <p key={idea} className="rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                  {idea}
                </p>)}
            </div>
          </div>

          <p className="text-xs leading-5 text-slate-400">
            {result.disclaimer}
          </p>
        </div> : null}
    </section>;
}